/**
 * Derived from confluence-plugins/confluence-bundled-plugins/confluence-inline-tasks/target/classes/js/editor-plugin/autocomplete-manager.js
 */
require('core-js/modules/es6.object.assign');

import window from 'window-or-global';
import { trigger } from '@confluence-classic/confluence-event_DO_NOT_USE';

import { KeyboardEventKeys } from '@confluence/keyboard-events';
import { setUserAgent, isIE } from '@confluence/browser-helper';

import { getRte, getAutocompleter } from '../../../tech-debt/facades/editor';
import { addClass } from '../../../helpers/dom';

setUserAgent();

const SETTING_DEFAULT = {
	leadingChar: '', //leading character inserting before autocomplete
	isDataValid: () => false, //function to check a string is valid or not,
	backWords: 0,
	onBeforeDie: () => {},
};

const AUTOCOMPLETE_TRIGGER_ID = '#autocomplete-trigger';
const AUTOCOMPLETE_DIRTY_CLASS = 'isDirty';

/**
 * This class is simplified for tinyMce-autocomplete-manager.js
 * Because tinyMce-autocomplete-manager.js tightly go along with AJS.inputDrivenDropdown
 */
export default class AutoCompleteManager {
	constructor(options) {
		this.ed = getRte().getEditor();
		this.settings = Object.assign({}, SETTING_DEFAULT, options);

		//each instance of AutoComplete.Manager have a 'control' property.
		//at the same time, in editor should have only active control across all instances of AutoCompleteManager
		//Therefore, you can consider 'control' as static property of AutoCompleteManager
		this.control = null;
		this.picker = null;
	}

	/**
	 * Init Autocomplete Control object and bind some default events for it
	 */
	start() {
		console.debug('startAutoComplete (FE) - Started');
		this.control = getAutocompleter().Control(this.ed, this.settings);

		if (!this.control) {
			return;
		}

		trigger('mention-autocomplete-on-start');

		this.attachEventsToControl();
	}

	attachEventsToControl() {
		const that = this;
		const autoCompleteControl = that.control;

		//inject onBeforeDie into this.control.die
		if (typeof this.settings.onBeforeDie === 'function') {
			const oldDie = this.control.die;
			this.control.die = function (...args) {
				that.settings.onBeforeDie.apply(that.control, args);
				oldDie.apply(that.control, args);
			};
		}

		const eventDefaultForAutoCompleteControl = {
			onBeforeKey(e, text) {
				//if user press anything, except ESCAPE
				//it means that user tries to edit it
				if (e.keyCode !== KeyboardEventKeys.ESCAPE.which) {
					that.control && addClass(that.control.getContainer()[0], AUTOCOMPLETE_DIRTY_CLASS);
				}

				//Delegate 3 keys: DOWN, UP and Enter when keydown
				if (e.keyCode === KeyboardEventKeys.DOWN.which) {
					this.settings.onKeyDown && this.settings.onKeyDown();

					window.tinymce.dom.Event.cancel(e);
					return false;
				}

				if (e.keyCode === KeyboardEventKeys.UP.which) {
					this.settings.onKeyUp && this.settings.onKeyUp();

					window.tinymce.dom.Event.cancel(e);
					return false;
				}

				if (e.keyCode === KeyboardEventKeys.ENTER.which) {
					that.settings.onBeforeEnter && that.settings.onBeforeEnter(that.ed.undoManager);
					this.settings.onKeyEnter && this.settings.onKeyEnter();

					window.tinymce.dom.Event.cancel(e);
					return false;
				}

				if (e.keyCode === KeyboardEventKeys.TAB.which) {
					this.settings.onKeyTab && this.settings.onKeyTab();

					window.tinymce.dom.Event.cancel(e);
					return false;
				}

				// User has key-downed backspace but text is *already* blank - close autocomplete.
				if (
					e.keyCode === KeyboardEventKeys.ESCAPE.which ||
					(e.keyCode === KeyboardEventKeys.BACKSPACE.which && !text)
				) {
					console.debug('autoCompleteControl.onBeforeKey - killing autoCompleteControl');
					this.die(e.keyCode === KeyboardEventKeys.BACKSPACE.which);
					return false;
				}

				return true;
			},

			onKeyPress(e) {
				const ch = isIE() ? e.keyCode : e.which;

				if (e.keyCode === KeyboardEventKeys.ENTER.which) {
					window.tinymce.dom.Event.cancel(e);
					return false;
				}

				const character = String.fromCharCode(ch); // charCode back to '@'
				const endCharIndex = this.settings.endChars.indexOf(character);
				if (endCharIndex >= 0) {
					console.debug(
						'autoCompleteControl.onKeyPress - caught autocomplete-closing char - character',
					);
					autoCompleteControl.die();
				}

				return true;
			},

			onAfterKey(e, text) {
				//prevent modifying trigger text
				const autocompleteContainer = that.control.getContainer()[0];
				const triggerSpan = autocompleteContainer.querySelectorAll(AUTOCOMPLETE_TRIGGER_ID);
				const triggerText = triggerSpan[0].innerText;

				if (triggerSpan.length > 0 && triggerText !== that.settings.leadingChar) {
					console.debug('after', 'dying because of: trigger text is modified');
					that.reset();
					return false;
				}

				if (e.keyCode === KeyboardEventKeys.ENTER.which) {
					window.tinymce.dom.Event.cancel(e);
					return false;
				} else {
					this.settings.onTextChange && this.settings.onTextChange(triggerText, text);
				}

				return true;
			},

			onDeath() {
				console.debug('autoCompleteControl.onDeath called');
				that.control = null;

				trigger('mention-autocomplete-on-death');
			},

			onScroll() {
				that.picker && that.picker.reposition();
			},
		};

		Object.assign(autoCompleteControl, eventDefaultForAutoCompleteControl);
	}

	/**
	 * Reset or destroy Autocomplete Control object
	 */
	reset() {
		if (!this.control) {
			return;
		}

		this.control.die();
		this.control = null;
	}
}
